<script lang="ts" setup>
import { Field, useFieldArray, useForm } from 'vee-validate'
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { RButton, RInputText } from '#atoms'
import { HotelRoomingListGuestStatus } from '#types'
import { validators } from '#utils'

import type { RHotelRoomingListGuestsProps } from './RHotelRoomingListGuests.types.ts'
import type { HotelRoomingListGuestResponseDto } from '@revolugo/graphql/private'

const { t } = useI18n()

const emits = defineEmits<{
  'update-rooming-list-guest': [HotelRoomingListGuestResponseDto]
  'delete-rooming-list-guest': [{ hotelRoomingListGuestId: string }]
}>()

const props = defineProps<RHotelRoomingListGuestsProps>()

const status = defineModel<HotelRoomingListGuestStatus>('status')

const occupancy: number = props.occupancy || 1

function validateFullname(value: unknown): boolean | string {
  return validators.minLength((value as string) ?? '', 1)
}

const defaultGuest: Omit<
  HotelRoomingListGuestResponseDto,
  'createdAt' | 'updatedAt'
> = {
  fullname: '',
  hotelRoomingListId: props.hotelRoomingListId,
  id: '',
}

const initialValues: {
  hotelRoomingListGuests: Omit<
    HotelRoomingListGuestResponseDto,
    'createdAt' | 'updatedAt'
  >[]
} = {
  hotelRoomingListGuests: props.hotelRoomingListGuests.length
    ? props.hotelRoomingListGuests
    : [{ ...defaultGuest }],
}

useForm({
  initialValues,
})

const { fields, push, remove, replace } = useFieldArray<
  Omit<HotelRoomingListGuestResponseDto, 'createdAt' | 'updatedAt'>
>('hotelRoomingListGuests')

watch(
  () => props.hotelRoomingListGuests,
  (
    newGuests: Omit<
      HotelRoomingListGuestResponseDto,
      'createdAt' | 'updatedAt'
    >[],
  ) => {
    replace(newGuests.length ? newGuests : [{ ...defaultGuest }])
  },
  { immediate: true },
)
</script>

<template>
  <div
    v-for="(hotelRoomingListGuest, index) in fields"
    :key="index"
    class="mb-4"
    data-testid="r-rooming-list-guests"
  >
    <h5
      class="text-sm font-sans mb-1"
      data-testid="r-rooming-list-guests-title"
    >
      {{ t('guest', { n: index + 1 }) }}
    </h5>
    <div class="flex flex-row items-center gap-2">
      <Field
        type="text"
        :name="`hotelRoomingListGuests[${index}].fullname`"
        v-slot="{ field, meta, errors }"
        :rules="validateFullname"
      >
        <RInputText
          v-bind="field"
          :disabled="isDisabled"
          :invalid="errors.length > 0"
          :data-testid="`r-rooming-list-guests-fullname-${index}`"
          @input="status = HotelRoomingListGuestStatus.Changed"
          @blur="
            () => {
              emits(
                'update-rooming-list-guest',
                hotelRoomingListGuest.value as HotelRoomingListGuestResponseDto,
              )
            }
          "
        />
        <RButton
          class="shrink-0"
          :label="t('save')"
          @click="
            () => {
              emits(
                'update-rooming-list-guest',
                hotelRoomingListGuest.value as HotelRoomingListGuestResponseDto,
              )
            }
          "
          :disabled="
            isDisabled ||
            errors.length > 0 ||
            !meta.dirty ||
            ![
              HotelRoomingListGuestStatus.Changed,
              HotelRoomingListGuestStatus.Error,
              undefined,
            ].includes(status)
          "
          :data-testid="`r-rooming-list-guests-save-button-${index}`"
        />
        <RButton
          link
          rounded
          class="text-neutral-950 text-bold !p-0 !w-6 shrink-0"
          icon="icon-[ph--trash-simple]"
          :aria-label="t('delete')"
          @click="
            () => {
              emits('delete-rooming-list-guest', {
                hotelRoomingListGuestId: (
                  hotelRoomingListGuest.value as HotelRoomingListGuestResponseDto
                ).id,
              })
              remove(index)
            }
          "
          size="small"
          :disabled="
            isDisabled ||
            !fields[0].value.fullname ||
            fields[0].value.fullname.length === 0
          "
          :data-testid="`r-rooming-list-guests-delete-button-${index}`"
        />
      </Field>
    </div>
  </div>

  <RButton
    v-if="!isFull && fields.length < occupancy"
    :disabled="isDisabled"
    link
    class="text-blue-700 font-medium text-sm !p-0"
    severity="info"
    :label="t('add')"
    icon="icon-[ph--plus]"
    iconify
    icon-class="mr-1"
    @click="push({ ...defaultGuest })"
    data-testid="r-rooming-list-guests-add-button"
  />
</template>

<i18n lang="json" src="./RHotelRoomingListGuests.i18n.json"></i18n>
